import React, { useState, useCallback } from 'react';
import { message } from 'antd';
import { postDataRequest } from '../../api/serviceMethods';
import { PRESIGNED_UPLOAD_URL } from '../../api/URLs';

/**
 * Helper to get video duration from a File object
 */
const getVideoDuration = (file) => {
    return new Promise((resolve) => {
        try {
            const video = document.createElement("video");
            video.preload = "metadata";
            const fileURL = URL.createObjectURL(file);
            video.src = fileURL;
            video.onloadedmetadata = () => {
                URL.revokeObjectURL(fileURL);
                resolve(video.duration);
            };
            video.onerror = () => {
                URL.revokeObjectURL(fileURL);
                resolve(0);
            };
        } catch (error) {
            resolve(0);
        }
    });
};

/**
 * OSSUploadWrapper
 * 
 * A reusable wrapper component that handles direct upload to Alibaba Cloud OSS.
 */
const OSSUploadWrapper = ({ children, onUploadSuccess, onUploadError, allowType, fileSize }) => {
    const [loading, setLoading] = useState(false);

    // Extract validation props from child if not explicitly provided to wrapper
    const effectiveAllowType = allowType || (React.isValidElement(children) ? children.props.allowType : undefined);
    const effectiveFileSize = fileSize || (React.isValidElement(children) ? children.props.fileSize : undefined);

    const handleOSSUpload = useCallback(async (options) => {
        const { file, onSuccess, onError, onProgress } = options;

        // 1. Validation (file type and max size)
        if (effectiveAllowType && effectiveAllowType.length > 0) {
            const fileName = (file.name || "").toLowerCase();
            const ext = fileName.substring(fileName.lastIndexOf('.'));
            const fileType = (file.type || "").toLowerCase();

            const isAllowed = effectiveAllowType.some(type => {
                const t = type.toLowerCase();
                if (t.startsWith('.')) {
                    return ext === t;
                }
                if (t.includes('gif')) {
                    return ext === '.gif' || fileType.includes('gif');
                }
                return fileType === t || ext === `.${t.split('/')[1]}`;
            });

            if (!isAllowed) {
                const errorMsg = `File type ${file.type || ext} is not allowed.`;
                message.error(errorMsg);
                if (onError) onError(new Error(errorMsg));
                return;
            }
        }

        if (effectiveFileSize) {
            const isLt = file.size / 1024 / 1024 < effectiveFileSize;
            if (!isLt) {
                const errorMsg = `File must be smaller than ${effectiveFileSize}MB!`;
                message.error(errorMsg);
                if (onError) onError(new Error(errorMsg));
                return;
            }
        }

        try {
            setLoading(true);
            
            // 2. Metadata Extraction (Duration for videos)
            let duration = 0;
            if (file.type.startsWith('video/')) {
                duration = await getVideoDuration(file);
            }

            // 3. Get Presigned URL from Backend
            const res = await postDataRequest(PRESIGNED_UPLOAD_URL, { fileName: file.name });
            
            if (!res || !res.signUrl) {
                throw new Error(res?.message || "Failed to get presigned upload URL.");
            }

            const { signUrl, path } = res;

            // 4. Direct Upload to OSS via XMLHttpRequest (for progress tracking)
            const xhr = new XMLHttpRequest();
            
            const uploadPromise = new Promise((resolve, reject) => {
                xhr.open('PUT', signUrl, true);
                xhr.setRequestHeader('Content-Type', 'application/octet-stream');

                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable && onProgress) {
                        const percent = Math.floor((event.loaded / event.total) * 100);
                        console.log("percent =", percent)
                        onProgress({ percent });
                    }
                };

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve();
                    } else {
                        reject(new Error(`Upload to OSS failed with status ${xhr.status}`));
                    }
                };

                xhr.onerror = () => reject(new Error('Upload to OSS failed due to a network error.'));
                xhr.send(file);
            });

            await uploadPromise;

            // 5. Construct the result
            const fileUrl = signUrl.split('?')[0];

            const result = {
                ...file,
                uid: file.uid,
                name: file.name,
                status: 'done',
                url: fileUrl,
                path: path,
                type: file.type,
                size: file.size,
                duration: Math.floor(duration)
            };

            if (onSuccess) onSuccess(result, file);
            if (onUploadSuccess) onUploadSuccess(result, file);
            
        } catch (error) {
            console.error('OSS Direct Upload Error:', error);
            if (onError) onError(error);
            if (onUploadError) onUploadError(error);
            message.error(error?.message || 'Upload failed.');
        } finally {
            setLoading(false);
        }
    }, [onUploadSuccess, onUploadError, effectiveAllowType, effectiveFileSize]);

    // Ensure we always pass a boolean for the loading prop
    const isProcessing = !!loading;

    if (typeof children === 'function') {
        return children({ customRequest: handleOSSUpload, loading: isProcessing });
    }

    if (React.isValidElement(children)) {
        return React.cloneElement(children, { 
            customRequest: handleOSSUpload, 
            loading: !!(isProcessing || children.props.loading)
        });
    }

    return children;
};

export default OSSUploadWrapper;
